import type {
  CreateUpdateSimpleUserDto,
  UpdateCurrentUserDto,
  UpdateCurrentUserPasswordDto,
  UpdateLanguageDto,
} from '@geartracker/dto';
import type { BaseFolder, GeneratedDocument, User } from '@geartracker/types';

import { useSnackbar } from 'src/components/snackbar';
import { useTranslate } from 'src/locales/use-locales';
import axios, { endpoints } from 'src/utils/axios';
import { getErrorMessage } from 'src/utils/error-message';
import { equipmentKeys } from './equipment.api';
import { eventKeys } from './events.api';
import { statsKeys } from './stats.api';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type FolderAccessEntry = {
  id: number;
  name: string;
  description: string | null;
  isAccessibleByOrganization: boolean;
  depth: number;
  path: string;
  parentId: number | null;
  parent?: BaseFolder | null;
  children: BaseFolder[];
  accessType: 'organization' | 'explicit' | 'none';
  _count: { Equipment: number };
};

export type UserFolderAccessResponse = {
  isAdmin: boolean;
  folders: FolderAccessEntry[];
};

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (type: string) => [...userKeys.lists(), { type }] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: number) => [...userKeys.details(), id] as const,
  me: () => [...userKeys.details(), 'me'] as const,
  folderAccess: (id: number) =>
    [...userKeys.detail(id), 'folder-access'] as const,
  documents: (id: number) => [...userKeys.detail(id), 'documents'] as const,
};

/**
 * Current user as returned by GET /users/me.
 * The endpoint intentionally strips `Organization` (org data is fetched
 * separately via `useOrganization`), so it is not available here.
 */
export type CurrentUser = Omit<User, 'Organization'>;

/**
 * Get Current User
 */
export async function getCurrentUser(): Promise<CurrentUser> {
  const response = await axios.get(endpoints.users.me.root);
  return response.data;
}

export const useCurrentUser = (enabled = true) => {
  return useQuery({
    queryKey: userKeys.me(),
    queryFn: getCurrentUser,
    enabled,
  });
};

/**
 * Update current user
 */
const updateCurentUser = async (user: UpdateCurrentUserDto): Promise<User> => {
  const response = await axios.patch(endpoints.users.me.root, user);
  return response.data;
};

export const useUpdateCurrentUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.update');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCurentUser,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.me() });
      void queryClient.invalidateQueries({
        queryKey: userKeys.detail(data.id),
      });
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Update current user language
 */
const updateCurentUserLanguage = async (
  data: UpdateLanguageDto,
): Promise<void> => {
  await axios.post(endpoints.users.me.language, data);
};

export const useUpdateCurrentUserLanguage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.update');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCurentUserLanguage,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 *
 * Change current user password
 */
const changeCurrentUserPassword = async (
  data: UpdateCurrentUserPasswordDto,
): Promise<void> => {
  await axios.post(endpoints.users.me.changePassword, data);
};

export const useChangeCurrentUserPassword = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.updatePassword');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: changeCurrentUserPassword,
    onSuccess: () => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Delete own user account
 */
const deleteOwnUser = async (): Promise<boolean> => {
  const response = await axios.post(endpoints.users.me.deleteAccount);
  return response.data;
};

export const useDeleteOwnUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.delete');

  return useMutation({
    mutationFn: deleteOwnUser,
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Get User
 */
export async function getUser(id: number): Promise<User> {
  const response = await axios.get(endpoints.users.root(id));
  return response.data;
}

export const useUser = (id: number) => {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => getUser(id),
  });
};

/**
 * Update user
 */
const updateUser = async ({
  userId,
  user,
}: {
  userId: number;
  user: CreateUpdateSimpleUserDto;
}): Promise<User> => {
  const response = await axios.patch(endpoints.users.root(userId), user);
  return response.data;
};

export const useUpdateUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.update');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({
        queryKey: userKeys.detail(data.id),
      });
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: userKeys.folderAccess(data.id),
      });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Get all users
 *
 */
export async function getAllUsers(): Promise<User[]> {
  const response = await axios.get(endpoints.users.root());
  return response.data;
}

export const useAllUsers = (enabled = true) => {
  return useQuery({
    queryKey: userKeys.list('all'),
    queryFn: getAllUsers,
    enabled,
  });
};

/**
 * Get all available users
 */
export async function getAllAvailableUsers(): Promise<User[]> {
  const response = await axios.get(endpoints.users.available);
  return response.data;
}

export const useAllAvailableUsers = (enabled = true) => {
  return useQuery({
    queryKey: userKeys.list('available'),
    queryFn: getAllAvailableUsers,
    enabled,
  });
};

/**
 * Create user
 */
const createUser = async (user: CreateUpdateSimpleUserDto): Promise<User> => {
  const response = await axios.post(endpoints.users.root(), user);
  return response.data;
};

export const useCreateUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.create');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: statsKeys.all });
      void queryClient.invalidateQueries({
        queryKey: userKeys.folderAccess(data.id),
      });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Deactivate user
 */
const deactivateUser = async (id: number): Promise<User> => {
  const response = await axios.post(endpoints.users.deactivate(id));
  return response.data;
};

export const useDeactivateUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.deactivate');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deactivateUser,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: userKeys.detail(data.id),
      });
      // Because deactivaed users are unlinked to their equipment,
      //we need to invalidate ALL the equipment,
      //we cannot predict which equipment is linked to the user
      void queryClient.invalidateQueries({ queryKey: equipmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

export type DeactivateUserGroupResult = {
  deactivatedUserIds: number[];
  skippedUserIds: number[];
};

const deactivateUserGroup = async (
  userIds: number[],
): Promise<DeactivateUserGroupResult> => {
  const response = await axios.post(endpoints.users.deactivateGroup, {
    userIds,
  });
  return response.data;
};

export const useDeactivateUserGroup = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.deactivate');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deactivateUserGroup,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      for (const id of data.deactivatedUserIds) {
        void queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      }
      void queryClient.invalidateQueries({ queryKey: equipmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: eventKeys.all });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Reactivate user
 */
const reactivateUser = async (id: number): Promise<User> => {
  const response = await axios.post(endpoints.users.reactivate(id));
  return response.data;
};

export const useReactivateUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.reactivate');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reactivateUser,
    onSuccess: (data) => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: userKeys.detail(data.id),
      });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Invite again user
 */
const inviteUser = async (id: number): Promise<User> => {
  const response = await axios.post(endpoints.users.invite(id));
  return response.data;
};

export const useInviteUser = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.invite');

  return useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      enqueueSnackbar(t('success'));
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};

/**
 * Get user folder access
 */
const getUserFolderAccess = async (
  userId: number,
): Promise<UserFolderAccessResponse> => {
  const response = await axios.get(endpoints.users.folderAccess(userId));
  return response.data;
};

export const useUserFolderAccess = (userId: number, enabled = true) => {
  return useQuery({
    queryKey: userKeys.folderAccess(userId),
    queryFn: () => getUserFolderAccess(userId),
    enabled,
  });
};

/**
 * Get the documents concerning a user (e.g. transfer certificates
 * where they were the recipient)
 */
const getUserDocuments = async (
  userId: number,
): Promise<GeneratedDocument[]> => {
  const response = await axios.get(endpoints.users.documents(userId));
  return response.data;
};

export const useUserDocuments = (userId: number, enabled = true) => {
  return useQuery({
    queryKey: userKeys.documents(userId),
    queryFn: () => getUserDocuments(userId),
    enabled,
  });
};

export type InviteUserGroupResult = {
  invitedUserIds: number[];
  skippedUserIds: number[];
};

const inviteUserGroup = async (
  userIds: number[],
): Promise<InviteUserGroupResult> => {
  const response = await axios.post(endpoints.users.inviteGroup, {
    userIds,
  });
  return response.data;
};

export const useInviteUserGroup = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate('api.user.invite');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteUserGroup,
    onSuccess: () => {
      enqueueSnackbar(t('success'));
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
    onError(error) {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(t(errorMessage, { defaultValue: t('error') }), {
        variant: 'error',
      });
    },
  });
};
